import { FormHelperText } from "@mui/material";
import { useFormikContext, withFormik } from "formik";
import { StatusCodes } from "http-status-codes";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import { useEmployeeRepository } from "../employee/useEmployeeRepository";
import { CitySelect } from "../localization/CitySelect";
import { ProvinceSelect } from "../localization/ProvinceSelect";
import { PositionSelect } from "../position/PositionSelect";
import { OrButton } from "../shared_modules/ui/components/atoms/or-button/or-button";
import { OrCheckbox } from "../shared_modules/ui/components/atoms/or-checkbox/or-checkbox";
import { MdiEye, MdiEyeOff } from "../shared_modules/ui/components/atoms/or-icon";
import { OrInput } from "../shared_modules/ui/components/atoms/or-input/or-input";
import { OrToastContainer, showOrToast } from "../shared_modules/ui/components/atoms/or-toast/or-toast";
import "./auth-ui.css";
import { useRegistrationVerification } from "./registration-verification-context";

export function EmployeeRegister({ successPath = "/", theme = "default" }) {
  const { createEmployee } = useEmployeeRepository();
  const { setPendingVerificationEmail } = useRegistrationVerification();

  const navigate = useNavigate();
  const isCaptchaEnabled = process.env.NODE_ENV === "production";
  const passwordToggleClassName =
    theme === "social" ? "or-auth-password-toggle or-auth-password-toggle--social" : "or-auth-password-toggle";

  const validationsForm = {
    name: yup.string().required("El nombre es requerido"),
    surname: yup.string().required("Los apellidos son requerido"),
    city: yup.string().required("La ciudad es requerida"),
    province: yup.string().required("La provincia es requerida"),
    position: yup.object().shape({
      id: yup.string().required("El puesto/cargo es requerido"),
      name: yup.string().required("El puesto/cargo es requerido"),
    }),
    email: yup.string().email("Escribe un email válido").required("El email es requerido"),
    password: yup
      .string()
      .min(8, "La contraseña es demasiado corta. debe tener un mínimo de 8 caracteres.")
      .required("La password es requerida"),
    confirmpassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "Las contraseñas deben coincidir")
      .required("La password es requerida"),
    policy: yup.boolean().oneOf([true], "Debes aceptar los términos y condiciones").required(),
    captcha: isCaptchaEnabled ? yup.string().required("El captcha es requerido") : yup.string(),
  };

  const FormView = (props) => {
    const { setFieldValue } = useFormikContext();

    const handleRecaptchaChange = (value) => {
      setFieldValue("captcha", value);
    };

    const { values, touched, errors, isSubmitting, handleChange, handleSubmit } = props;

    return (
      <form className="or-auth-form" onSubmit={handleSubmit}>
        <div className="or-auth-form__group">
          <OrInput
            id="name"
            inputType="text"
            label="Nombre *"
            name="name"
            onChange={handleChange}
            theme={theme}
            value={values.name}
            hasError={touched.name && Boolean(errors.name)}
            errorText={touched.name ? errors.name : ""}
          />
        </div>
        <div className="or-auth-form__group">
          <OrInput
            id="surname"
            inputType="text"
            label="Apellidos *"
            name="surname"
            onChange={handleChange}
            theme={theme}
            value={values.surname}
            hasError={touched.surname && Boolean(errors.surname)}
            errorText={touched.surname ? errors.surname : ""}
          />
        </div>
        <ProvinceSelect touched={touched} errors={errors} value={values.province} theme={theme} />
        <CitySelect touched={touched} errors={errors} value={values.city} provinceSelected={values.province} theme={theme} />
        <PositionSelect touched={touched} errors={errors} value={values.position} theme={theme} />
        <div className="or-auth-form__group">
          <OrInput
            onChange={handleChange}
            id="email"
            inputType="email"
            label="Email *"
            name="email"
            theme={theme}
            value={values.email}
            hasError={touched.email && Boolean(errors.email)}
            errorText={touched.email ? errors.email : ""}
          />
        </div>
        <div className="or-auth-form__group">
          <OrInput
            id="password"
            inputType={values.showPassword ? "text" : "password"}
            label="Contraseña *"
            name="password"
            onChange={handleChange}
            placeholder="********************"
            theme={theme}
            value={values.password}
            hasError={touched.password && Boolean(errors.password)}
            errorText={touched.password ? errors.password : ""}
            rightSlot={
              <button
                className={passwordToggleClassName}
                id="passwordicon"
                type="button"
                aria-label="toggle password visibility"
                onClick={() => setFieldValue("showPassword", !values.showPassword)}
              >
                {values.showPassword ? <MdiEyeOff /> : <MdiEye />}
              </button>
            }
          />
        </div>
        <div className="or-auth-form__group">
          <OrInput
            id="confirmpassword"
            inputType={values.showRepeatedPassword ? "text" : "password"}
            label="Repite la contraseña *"
            name="confirmpassword"
            onChange={handleChange}
            placeholder="********************"
            theme={theme}
            value={values.confirmpassword}
            hasError={touched.confirmpassword && Boolean(errors.confirmpassword)}
            errorText={touched.confirmpassword ? errors.confirmpassword : ""}
            rightSlot={
              <button
                className={passwordToggleClassName}
                id="confirmpasswordicon"
                type="button"
                aria-label="toggle password visibility"
                onClick={() => setFieldValue("showRepeatedPassword", !values.showRepeatedPassword)}
              >
                {values.showRepeatedPassword ? <MdiEyeOff /> : <MdiEye />}
              </button>
            }
          />
        </div>
        {isCaptchaEnabled && (
          <div className="or-auth-form__group or-auth-form__group--terms">
            <div className="or-auth-form__recaptcha">
              <ReCAPTCHA sitekey={process.env.RECAPTCHA_SITE_KEY} onChange={handleRecaptchaChange} />
            </div>
            {touched.captcha && Boolean(errors.captcha) && <FormHelperText error>{errors.captcha}</FormHelperText>}
          </div>
        )}
        <div className="or-auth-form__group or-auth-form__group--terms">
          <OrCheckbox
            checked={Boolean(values.policy)}
            className="or-auth-checkbox"
            error={touched.policy && Boolean(errors.policy) ? errors.policy : undefined}
            id="policy"
            theme={theme}
            label={
              <>
                Acepto los{" "}
                <a
                  href="https://openreferences.com/aviso-legal/"
                  target="_blank"
                  className="or-auth-policy-link"
                  rel="noreferrer"
                >
                  terminos y condiciones
                </a>
                , y{" "}
                <a
                  className="or-auth-policy-link"
                  target="_blank"
                  href="https://openreferences.com/politica-de-privacidad/"
                  rel="noreferrer"
                >
                  política de privacidad
                </a>
              </>
            }
            name="policy"
            onChange={handleChange}
          />
        </div>
        <div className="or-auth-form__group">
          <OrButton type="submit" fullWidth isLoading={isSubmitting} size="lg" theme={theme} variant="primary">
            Regístrarme
          </OrButton>
        </div>
      </form>
    );
  };

  const Form = withFormik({
    mapPropsToValues: ({
      name,
      surname,
      city,
      province,
      position,
      email,
      password,
      confirmpassword,
      showPassword,
      showRepeatedPassword,
      policy,
      captcha,
    }) => {
      return {
        name: name || "",
        surname: surname || "",
        city: city || "",
        province: province || "",
        position: position || "",
        email: email || "",
        password: password || "",
        confirmpassword: confirmpassword || "",
        showPassword: showPassword || false,
        showRepeatedPassword: showRepeatedPassword || false,
        policy: policy || false,
        captcha: captcha || "",
      };
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape(validationsForm),

    handleSubmit: async (values, { setSubmitting }) => {
      try {
        let response = await createEmployee({
          id: uuidv4(),
          username: values.email,
          password: values.password,
          name: values.name,
          surname: values.surname,
          city: values.city,
          province: values.province,
          position: values.position.id,
          captcha: isCaptchaEnabled ? values.captcha : "",
        });

        if (response.status !== StatusCodes.CREATED) {
          setSubmitting(false);
          return;
        }

        showOrToast("Usuario creado. Por favor, revisa tu bandeja de entrada para validar el correo");
        setPendingVerificationEmail(values.email);
        navigate(successPath);
      } catch (error) {
        showOrToast(error.response.data.message || "Error al crear la cuenta", { type: "error" });
      }
    },
  })(FormView);

  return (
    <>
      <OrToastContainer />
      <Form />
    </>
  );
}
